import { Component, EventEmitter, HostBinding, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogService } from '@nebular/theme';
import { MetricKey, MetricQuery, MetricResult, MetricScope, DashboardLayout, WidgetLayout } from '../../interfaces/metrics';
import { MetricsService } from '../../shared/metrics.service';
import { NotifierService } from '../../shared/notifier.service';
import { DrilldownPanelComponent } from '../drilldown/drilldown-panel.component';
import { StatusChip } from '../widgets/kpi-card/kpi-card.component';
import { WidgetHostComponent } from '../widget-host/widget-host.component';

type SelfWidgetKey =
  | 'hours.week' | 'utilization.self' | 'timesheet.status'
  | 'hours.heatmap' | 'hours.byday' | 'assignments.ending'
  | 'leave.self' | 'contribution.self';

// Own-self default on a 12-col grid. MIRROR of MetricsService::SELF_DEFAULT_LAYOUT (the BE
// map is authoritative; this only drives pre-GET first paint + the Reset draft). Keep the two
// byte-identical.
//
// v2.2 cut: Personal renders four tiles over two full rows. hours.byday's bars moved inside
// the hours.week tile, and hours.heatmap / leave.self / contribution.self no longer render
// on own-self at all. Their entries stay here because this Record is keyed by every
// SelfWidgetKey and because the member-drill still positions heatmap, leave and
// contribution from DRILL_DEFAULT_LAYOUT(_WITH_CONTRIB) below.
const SELF_DEFAULT_LAYOUT: Record<SelfWidgetKey, WidgetLayout> = {
  'hours.week':         { x: 0, y: 0,  w: 8,  h: 4 },
  'timesheet.status':   { x: 8, y: 0,  w: 4,  h: 4 },
  'assignments.ending': { x: 0, y: 4,  w: 8,  h: 4 },
  'utilization.self':   { x: 8, y: 4,  w: 4,  h: 4 },
  // Catalog-only below this line: not rendered on own-self.
  'hours.byday':        { x: 0, y: 8,  w: 12, h: 3 },
  'hours.heatmap':      { x: 0, y: 11, w: 12, h: 3 },
  'leave.self':         { x: 0, y: 14, w: 6,  h: 3 },
  'contribution.self':  { x: 6, y: 14, w: 6,  h: 3 },
};

// Gapless default for the member-drill (isSelf=false), which renders hours.week (with its
// merged daily bars), timesheet.status, utilization.self, assignments.ending, hours.heatmap
// and leave.self (tenant-scoped since Holiday.Client_id; the drill is its only render
// surface — the v2.2 cut keeps it off own-self). contribution.self only when the actor
// holds view_bill_rate, which is the variant below.
const DRILL_DEFAULT_LAYOUT: Record<string, WidgetLayout> = {
  'hours.week':         { x: 0, y: 0, w: 8, h: 4 },
  'timesheet.status':   { x: 8, y: 0, w: 4, h: 4 },
  'utilization.self':   { x: 0, y: 4, w: 4, h: 4 },
  'assignments.ending': { x: 4, y: 4, w: 8, h: 4 },
  'hours.heatmap':      { x: 0, y: 8, w: 8, h: 3 },
  'leave.self':         { x: 8, y: 8, w: 4, h: 3 },
};

// Gapless default for the member-drill when the actor holds view_bill_rate
// (contribution.self renders): assignments.ending narrows to make room, still filling the
// row exactly.
const DRILL_DEFAULT_LAYOUT_WITH_CONTRIB: Record<string, WidgetLayout> = {
  ...DRILL_DEFAULT_LAYOUT,
  'contribution.self':  { x: 4, y: 4, w: 4, h: 4 },
  'assignments.ending': { x: 8, y: 4, w: 4, h: 4 },
};

// Min column span per widget so text/tables never crush when resized.
// assignments.ending is a 4-column table; below w=4 its month column gets narrower than
// the status pill and the row degrades however the cell is laid out, so 4 is the floor.
// hours.week is now a scalar plus a bar chart side by side, so it needs the same floor.
const SELF_MIN_W: Record<SelfWidgetKey, number> = {
  'hours.week': 4, 'utilization.self': 2, 'timesheet.status': 2,
  'hours.heatmap': 4, 'hours.byday': 4, 'assignments.ending': 4,
  'leave.self': 2, 'contribution.self': 2,
};

const SELF_WIDGET_ORDER = Object.keys(SELF_DEFAULT_LAYOUT) as SelfWidgetKey[];

@Component({
  selector: 'bst-self-dashboard-grid',
  templateUrl: './self-dashboard-grid.component.html',
  styleUrls: ['../dashboard.component.scss', './self-dashboard-grid.component.scss'],
  standalone: false,
})
export class SelfDashboardGridComponent implements OnInit {
  @Input() scope: MetricScope = { type: 'self' };
  @Input() anchor = '';
  // Own-self is coordinated with DashboardComponent's global period navigator.
  // A member drill retains its own period control because it is rendered in a drawer
  // without that parent navigator.
  @Input() period: 'week' | 'month' = 'week';
  @Output() periodChange = new EventEmitter<'week' | 'month'>();
  // Manager-drill only (isSelf=false): the ACTOR's (viewing manager's) own view_bill_rate
  // grant for the current tenant. When true, the drill also renders contribution.self for
  // the report being viewed (the BE resolve gate is the real enforcement; this only decides
  // whether the tile is drawn).
  @Input() actorCanSeeBillRate = false;
  // Present only in Week mode. Keep its result available to the Hours card's
  // drill-down without keeping a month-shaped query alive in Month mode.
  @ViewChild('hb') private hoursByDayHost?: WidgetHostComponent;

  // One Arrange mode (layout only; self has no per-tenant show/hide config). selfLayout is the
  // applied per-user layout; layoutDraft is the working copy the grid mutates while editing.
  editing = false;
  savingLayout = false;
  // False until the initial layout GET succeeds (covers still-loading and failed). Until then
  // selfLayout is {}, dragging is disabled, and Save skips the PUT so we can never overwrite a
  // stored layout with defaults.
  selfLayoutLoaded = false;
  // True once the initial GET has SETTLED (success OR failure). The grid is not rendered
  // until this is true (for the own-self view), so gridstack initializes ONCE over the
  // loaded layout instead of over the empty default, then having to resync when the layout
  // arrives late. A resync after gridstack has adopted the tiles does not reliably reapply
  // positions here, so we avoid needing it. On a failed load we still render (with defaults)
  // so the tab is never blank, but selfLayoutLoaded stays false so arrange/save stay off.
  selfLayoutFetched = false;
  selfLayout: DashboardLayout = {};
  // Content revision from the last read, echoed back on save so a second tab
  // cannot flatten an arrangement made after this one loaded (#1107).
  selfLayoutRevision?: string;
  layoutDraft: DashboardLayout = {};
  layoutResetTick = 0;

  constructor(
    private dialog: NbDialogService,
    private metrics: MetricsService,
    private notifier: NotifierService,
    public router: Router,
  ) {}

  ngOnInit(): void {
    // Only the user's OWN self view fetches + arranges. A manager drilling a report
    // (scope.type=user, isSelf=false) never fetches or persists. The endpoint also keys every
    // row by the actor id, so a report's layout can never be written from here.
    if (!this.isSelf) { return; }
    this.metrics.getDashboardLayout('self').subscribe({
      next: ({ layout, revision }) => {
        this.selfLayout = layout;
        this.selfLayoutRevision = revision;
        this.selfLayoutLoaded = true;
        this.selfLayoutFetched = true;   // renders the grid, now over the loaded layout
        // If the GET resolves AFTER Arrange was already entered, reseed the draft so a Save
        // writes the real layout, not the empty/default shown during the load window.
        if (this.editing) { this.layoutDraft = this.seedLayoutDraft(layout); }
      },
      error: err => {
        // Leave selfLayoutLoaded false: dragging stays disabled and Save skips the PUT, so a
        // failed load cannot overwrite a stored layout with defaults. Still render the grid
        // (selfLayoutFetched) so the tab is never blank, falling back to client defaults.
        this.selfLayoutFetched = true;
        console.error('self dashboard: layout load failed', err);
        this.notifier.notify('dashboard.self.layout.load_error', null, 'danger');
      },
    });
  }

  get isSelf(): boolean { return this.scope.type === 'self'; }
  // Draggable only in edit mode and only once a real layout has loaded.
  get layoutEditable(): boolean { return this.editing && this.selfLayoutLoaded; }

  // Grip visibility is toggled by ONE class on the component host, then a descendant CSS
  // selector, NOT a per-tile binding. gridstack-managed tiles that were created inside an
  // @if (hours.heatmap / contribution.self) do not receive per-tile Angular binding updates
  // once the grid inits over them, so a per-tile [class] never toggles for those two. The
  // host element is outside gridstack's reach, so its class updates reliably.
  @HostBinding('class.arranging') get arrangingClass(): boolean { return this.layoutEditable; }

  // Ordered signature of the rendered GRID tile set for the grid idsKey. Own-self is 4
  // after the v2.2 cut; the drill is 6, or 7 when the actor holds view_bill_rate.
  get visibleSelfKey(): string { return this.visibleIds().join(','); }

  private visibleIds(): string[] {
    if (this.isSelf) {
      // v2.2 cut: Personal renders exactly the four tiles that answer "Am I done this
      // week?". hours.byday's bars are drawn inside the hours.week tile; heatmap, leave
      // and contribution do not render here at all. All four remain in the catalog, so
      // re-adding one is this filter plus a default-layout slot.
      return SELF_WIDGET_ORDER.filter(id =>
        id === 'hours.week' || id === 'timesheet.status'
        || id === 'assignments.ending' || id === 'utilization.self');
    }
    // Member-drill: no standalone by-day tile (merged into hours.week), contribution
    // only when the ACTOR holds view_bill_rate. leave.self renders here (and only
    // here): tenant-scoped by the backend since Holiday.Client_id.
    return SELF_WIDGET_ORDER.filter(id =>
      id !== 'hours.byday'
      && (id !== 'contribution.self' || this.actorCanSeeBillRate));
  }

  // Grid position for a tile: own-self uses the draft (editing) or applied layout, falling back
  // to the self default; the drill uses the gapless drill default (the with-contribution variant
  // when the actor's grant renders that tile).
  cell(id: SelfWidgetKey): WidgetLayout {
    if (!this.isSelf) {
      const drillLayout = this.actorCanSeeBillRate ? DRILL_DEFAULT_LAYOUT_WITH_CONTRIB : DRILL_DEFAULT_LAYOUT;
      return drillLayout[id] ?? SELF_DEFAULT_LAYOUT[id];
    }
    return (this.editing ? this.layoutDraft : this.selfLayout)[id] ?? SELF_DEFAULT_LAYOUT[id];
  }
  minW(id: SelfWidgetKey): number { return SELF_MIN_W[id] ?? 1; }

  enterArrange(): void {
    // Seed from the RENDERED tile set only; see seedLayoutDraft for why.
    this.layoutDraft = this.seedLayoutDraft(this.selfLayout);
    this.editing = true;
  }
  cancelArrange(): void { this.editing = false; }

  // True when a rendered tile sits somewhere the current default does not put it.
  // Stored coordinates are never reconciled on read and never migrated: a stored cell
  // that happens to match an old default is indistinguishable from a deliberate one, so
  // moving it automatically would silently undo real arrangements. The consequence is
  // that anyone who ever arranged their dashboard stops receiving changed defaults,
  // which is exactly what happened to v2.2. This flag makes the choice visible instead
  // of leaving it buried behind Arrange then Reset.
  get defaultDiffers(): boolean {
    if (!this.isSelf || !this.selfLayoutLoaded) { return false; }
    return this.visibleIds().some(id => {
      const stored = this.selfLayout[id];
      const def = SELF_DEFAULT_LAYOUT[id as SelfWidgetKey];
      return !!stored && !!def
        && (stored.x !== def.x || stored.y !== def.y || stored.w !== def.w || stored.h !== def.h);
    });
  }

  // Adoption is a PREVIEW, not a write: this drops the user into Arrange with the default
  // seeded as the draft, so nothing is stored until they press Save and Cancel still
  // leaves their own arrangement intact.
  previewDefaultLayout(): void {
    this.enterArrange();
    this.resetLayout();
  }

  // Reset positions to the catalog default in the draft only (persisted on Save). Bump the tick
  // so the grid reloads even mid-edit (its normal resync is gated off while editing).
  resetLayout(): void {
    this.layoutDraft = this.seedLayoutDraft(SELF_DEFAULT_LAYOUT);
    this.layoutResetTick++;
  }

  // Filter a layout source (selfLayout, a GET response, or the default map) down to the
  // RENDERED tile set (visibleIds()), dropping hours.byday/hours.heatmap/leave.self/
  // contribution.self's dead entries on own-self: none of them render as a grid item there
  // (v2.2 cut), so nothing ever overrides them and effectiveLayout() always refills them
  // from their defaults on the next GET. Carrying a phantom cell into a draft
  // means a Save that never touches the grid PUTs it unchanged; if a real tile was ever
  // dragged onto one of their rows, the PUT then 422s on a same-row overlap the user can
  // neither see nor fix. Used by every draft-seeding path (enterArrange, the late-GET
  // reseed, and Reset) so there is exactly one place to get this filter right.
  private seedLayoutDraft(source: DashboardLayout): DashboardLayout {
    const draft: DashboardLayout = {};
    for (const id of this.visibleIds()) {
      if (source[id]) { draft[id] = source[id]; }
    }
    return draft;
  }

  saveLayout(): void {
    if (this.savingLayout) { return; }               // ignore reentrant Save clicks
    this.savingLayout = true;
    // Skip the PUT until a layout actually loaded, so we can never overwrite the stored layout
    // with defaults shown during the load window.
    if (!this.selfLayoutLoaded) { this.editing = false; this.savingLayout = false; return; }
    this.metrics.saveDashboardLayout('self', this.layoutDraft, this.selfLayoutRevision).subscribe({
      next: ({ layout, revision }) => {
        this.selfLayout = layout;
        this.selfLayoutRevision = revision;
        this.editing = false;
        this.savingLayout = false;
      },
      error: err => {
        if (err?.status === 409) {
          // Same dashboard arranged in another tab after this one loaded (#1107).
          // Show theirs instead of flattening it; the editor stays open.
          this.selfLayout = err.error?.layout ?? this.selfLayout;
          this.selfLayoutRevision = err.error?.revision;
          this.layoutDraft = this.seedLayoutDraft(this.selfLayout);
          this.notifier.notify('dashboard.company.layout.conflict', null, 'warning');
          this.savingLayout = false;
          return;
        }
        // Keep edit mode open + draft intact so the user retries without losing the arrangement;
        // nothing looks saved that was not.
        console.error('self dashboard: save layout failed', err?.error?.message ?? err);
        this.notifier.notify('dashboard.self.save_error', null, 'danger');
        this.savingLayout = false;
      },
    });
  }

  // One period for every widget that can actually answer in one (#1075). Widgets
  // used to carry a hardcoded week or month each, which is the inconsistency the
  // sprint review called out.
  //
  // Default week, to agree with the company dashboard: every company widget is
  // week, including its own utilization KPI and trend, so a self view defaulting
  // to month made the same metric read two different windows depending on which
  // dashboard you were looking at.
  private drillPeriod: 'week' | 'month' = 'week';
  get selectedPeriod(): 'week' | 'month' { return this.isSelf ? this.period : this.drillPeriod; }

  setPeriod(period: 'week' | 'month'): void {
    if (this.isSelf) { this.periodChange.emit(period); } else { this.drillPeriod = period; }
  }

  // Scope comes from the input; the period follows the switcher.
  q(key: MetricKey): MetricQuery {
    return { key, scope: this.scope, period: { type: this.selectedPeriod, anchor: this.anchor } };
  }

  // Week-granularity always (approval state is a weekly concept), but the anchor
  // follows prev/next: TimesheetStatusResolver reads the requested period, so
  // browsing back shows that week's status. Not qFixedWindow — pinning its anchor
  // would silently freeze the tile on the current week.
  qWeek(key: MetricKey): MetricQuery {
    return { key, scope: this.scope, period: { type: 'week', anchor: this.anchor } };
  }

  /**
   * For the as-of-today widgets, whose resolvers deliberately ignore the
   * requested period: HoursHeatmapResolver (always 16 closed weeks),
   * AssignmentsEndingResolver (always today's assignments, current calendar
   * month's hours — pinned by OperationalResolversTest
   * ::test_assignments_ending_ignores_the_requested_period) and LeaveResolver
   * (always weekdays taken this calendar year).
   *
   * They are deliberately NOT wired to the switcher. Wiring them would move the
   * control and leave tiles unchanged with nothing saying why, which is a worse
   * defect than the inconsistency being fixed. Each states its own window in its
   * subtitle.
   *
   * The anchor is pinned to load-time today, not this.anchor: widget-host
   * re-queries on any signature change, so a moving anchor would refetch these
   * tiles on every prev/next click to redraw exactly what is already on screen.
   */
  private readonly fixedAnchor = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam' }).format(new Date());

  qFixedWindow(key: MetricKey): MetricQuery {
    return { key, scope: this.scope, period: { type: 'week', anchor: this.fixedAnchor } };
  }

  // `extra` is a second result the caller already holds and the drawer should show as
  // evidence for the same card (hours.byday beside hours.week). The panel never fetches,
  // so without this the drawer can only ever repeat the card.
  openDrill(result: MetricResult | undefined, extra?: MetricResult): void {
    if (!result) { return; }
    this.dialog.open(DrilldownPanelComponent, { context: { result, extra }, dialogClass: 'drawer-right' });
  }

  openHoursDrill(result: MetricResult | undefined): void {
    this.openDrill(result, this.selectedPeriod === 'week' ? this.hoursByDayHost?.last : undefined);
  }

  goLogHours(): void {
    this.router.navigate(['/registration/hours/calendar']).catch(err => console.error('dashboard: navigation failed', err));
  }

  weekNo(iso: string): number {
    const d = new Date(iso + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7));
    const week1 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
    return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getUTCDay() + 6) % 7)) / 7);
  }

  tsChip(status: string): StatusChip {
    const s: StatusChip['status'] =
      status === 'submitted' || status === 'approved' ? 'good'
      : status === 'rejected' ? 'critical'
      : 'warning'; // 'open' (and any unknown) → warning
    return { status: s, labelKey: 'dashboard.ts_status.' + status };
  }
}
